import { css, jsx } from '@emotion/core';
import { Col, Row } from 'antd';
import { isEmpty } from 'lodash';
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';

import Error from '@/pages/_error';
import Blocks from '@/src/cms/components/CMSPage/CorePage/Blocks';
import HeroContent from '@/src/cms/components/CMSPage/CorePage/HeroContent';
import DesignPage from '@/src/cms/models/DesignPage';
import parseURL from '@/src/legalCases/utils';
import SearchResults from '@/src/namedParty/components/CasesSearchResults';
import SearchFilters from '@/src/namedParty/components/NamedPartySearchFilters';
import TagTile from '@/src/namedParty/components/TagTile';
import {
  getFieldOptions,
  loadResults,
  setCurrentPage,
  setSortField,
  setSortOrder,
  updateSearchCriteria,
} from '@/src/namedParty/redux/actions';
import AdminLink from '@/src/shared/components/AdminLink';
import Container from '@/src/shared/components/Container';
import Layout from '@/src/shared/components/Layout';
import Section from '@/src/shared/components/Section';
import WebLogImage from '@/src/shared/components/WebLogImage';
import { ADVOCACY_ACCESS, checkAccess } from '@/src/shared/constants';
import { space } from '@/styles';
import request from '@/utils/axios';

const style = {
  searchContainer: css`
    @media print {
      padding-bottom: 0px !important;
      padding-top: 0px !important;
    }
  `,
};

const FIELD_NAMES = {
  CASE_NAME: 'CaseNames',
  STATE: 'States',
  ISSUE: 'Issues',
  STATUS: 'Statuses',
};

const NamedParty = props => {
  const {
    fieldOptionsData: { options },
    sortField,
    sortOrder,
    searchData,
    searchCriteria,
    messageData = {},
    isLoggedIn,
    role,
    PersonID,
  } = props;
  const router = useRouter();
  const { query } = router;
  const { statuses, states, caseNames, issues } = options;

  const [mounted, setMounted] = useState(false);
  const [filterExpanded, setFilterExpanded] = useState(true);
  const [pagedata, setPageData] = useState({});
  const [loading, setLoading] = useState(true);

  const hasAccess = checkAccess(ADVOCACY_ACCESS.LEGAL_NAMED_PARTY, role);
  const isAuthorized = PersonID && Array.isArray(role) && hasAccess;
  const errorCode = isLoggedIn && !hasAccess ? 401.4 : 401;

  const handleSearchFiltersCollapse = activeTabs => {
    setFilterExpanded(activeTabs.includes('1'));
  };
  const handleCriteriaChange = (name, value) => {
    props.updateSearchCriteria(name, value);
  };

  const fetchDesignPage = async () => {
    const response = await request({
      url: `cms/designpage`,
      params: {
        alias: 'named-party',
      },
    });
    const { data } = response;
    setPageData(data);
    setLoading(false);
  };

  useEffect(() => {
    fetchDesignPage();
  }, []);

  useEffect(() => {
    if (mounted) return;
    if (!isEmpty(query)) {
      const searchPayload = parseURL(options, router, handleCriteriaChange);
      const payload = {
        searchOptions: {
          AmicusFieldOptions: {
            CaseTypes: [
              {
                CaseTypeCode: '2',
              },
            ],
            ...searchPayload,
            SortOrder: sortOrder,
            SortField: sortField,
          },
        },
      };
      props.loadResults(payload);
    }
    setMounted(true);
  }, [query]);

  const tagClose = (e, value, input) => {
    e.preventDefault();

    const fieldValues = searchCriteria[input];
    const updatedFieldValues = fieldValues.filter(v => v !== value);
    handleCriteriaChange(input, updatedFieldValues);
  };

  const handleSortOrder = async value => {
    await props.setCurrentPage(1);
    await props.setSortOrder(value);
    const payload = {
      searchOptions: {
        AmicusFieldOptions: {
          CaseTypes: [
            {
              CaseTypeCode: '2',
            },
          ],
          CaseNames: searchCriteria.CaseNames
            ? searchCriteria.CaseNames.map(s => {
                return { Value: s };
              })
            : [],
          Statuses: searchCriteria.Statuses
            ? searchCriteria.Statuses.map(s => {
                return { StatusCode: s };
              })
            : [],
          Issues: searchCriteria.Issues
            ? searchCriteria.Issues.map(i => {
                return { IssueId: i };
              })
            : [],
          States: searchCriteria.States
            ? searchCriteria.States.map(s => {
                return { StateCode: s };
              })
            : [],
          SortOrder: value,
          SortField: sortField,
          PageSize: 10,
          Page: 1,
        },
      },
    };
    props.loadResults(payload);
  };

  const handleSortField = async value => {
    await props.setCurrentPage(1);
    await props.setSortField(value);
    const payload = {
      searchOptions: {
        AmicusFieldOptions: {
          CaseTypes: [
            {
              CaseTypeCode: '2',
            },
          ],
          CaseNames: searchCriteria.CaseNames
            ? searchCriteria.CaseNames.map(s => {
                return { Value: s };
              })
            : [],
          Statuses: searchCriteria.Statuses
            ? searchCriteria.Statuses.map(s => {
                return { StatusCode: s };
              })
            : [],
          Issues: searchCriteria.Issues
            ? searchCriteria.Issues.map(i => {
                return { IssueId: i };
              })
            : [],
          States: searchCriteria.States
            ? searchCriteria.States.map(s => {
                return { StateCode: s };
              })
            : [],
          SortOrder: sortOrder,
          SortField: value,
          PageSize: 10,
          Page: 1,
        },
      },
    };
    props.loadResults(payload);
  };

  const handlePageChange = async page => {
    await props.setCurrentPage(page);
    const payload = {
      searchOptions: {
        AmicusFieldOptions: {
          CaseTypes: [
            {
              CaseTypeCode: '2',
            },
          ],
          CaseNames: searchCriteria.CaseNames
            ? searchCriteria.CaseNames.map(s => {
                return { Value: s };
              })
            : [],
          Statuses: searchCriteria.Statuses
            ? searchCriteria.Statuses.map(s => {
                return { StatusCode: s };
              })
            : [],
          Issues: searchCriteria.Issues
            ? searchCriteria.Issues.map(i => {
                return { IssueId: i };
              })
            : [],
          States: searchCriteria.States
            ? searchCriteria.States.map(s => {
                return { StateCode: s };
              })
            : [],
          SortOrder: sortOrder,
          SortField: sortField,
          PageSize: 10,
          Page: page,
        },
      },
    };
    props.loadResults(payload);
  };

  useEffect(() => {
    const fetchOptions = async () => {
      await props.getFieldOptions();
    };
    fetchOptions();
  }, []);

  const searchKeys = Object.keys(searchCriteria);
  const filterTags = [];

  searchKeys.forEach(k => {
    const fieldValue = searchCriteria[k];

    if (!Array.isArray(fieldValue)) return;
    if (k === FIELD_NAMES['CASE_NAME']) {
      fieldValue.forEach(v => {
        const selectedCase = caseNames.find(c => Number(c.Value) === Number(v));
        if (selectedCase) {
          const formattedState = {
            value: v,
            children: selectedCase.Text,
            input: k,
          };
          filterTags.push(formattedState);
        }
      });
    }

    if (k === FIELD_NAMES['STATE']) {
      fieldValue.forEach(v => {
        const selectedState = states.find(s => s.StateCode === v);
        if (selectedState) {
          const formattedState = {
            value: v,
            children: selectedState.StateDescription,
            input: k,
          };
          filterTags.push(formattedState);
        }
      });
    }

    if (k === FIELD_NAMES['ISSUE']) {
      fieldValue.forEach(v => {
        const selectedIssue = issues.find(i => i.IssueId === v);
        if (selectedIssue) {
          const formattedIssue = {
            value: v,
            children: selectedIssue.IssueName,
            input: k,
          };
          filterTags.push(formattedIssue);
        }
      });
    }

    if (k === FIELD_NAMES['STATUS']) {
      fieldValue.forEach(v => {
        const selectedStatus = statuses.find(i => i.StatusCode === v);
        if (selectedStatus) {
          const formattedIssue = {
            value: v,
            children: selectedStatus.StatusDescription,
            input: k,
          };
          filterTags.push(formattedIssue);
        }
      });
    }
  });

  if (loading) {
    return null;
  }
  if (!isLoggedIn || !isAuthorized) return <Error status={errorCode} />;

  const designPageData = new DesignPage({ ...pagedata, ...messageData });

  return (
    <Layout>
      <AdminLink pageID="514" pageType="core" />
      <HeroContent
        heroData={{
          ...designPageData,
        }}
      />
      {designPageData.Blocks.map(b => (
        <Blocks
          key={b}
          blockData={designPageData.entities.Blocks[b]}
          entities={designPageData.entities}
        />
      ))}
      <Section css={style.searchContainer}>
        <Container>
          <Row type="flex" gutter={{ xs: 24, md: 32 }}>
            <Col xs={24} lg={8}>
              <SearchFilters handleSearchFiltersCollapse={handleSearchFiltersCollapse} />
            </Col>
            <Col xs={24} lg={16}>
              <div css={space.m.b16}>
                {!filterExpanded &&
                  filterTags.map(tag => <TagTile key={tag.value} tag={tag} tagClose={tagClose} />)}
              </div>
              <SearchResults
                handleSortOrder={handleSortOrder}
                handleSortField={handleSortField}
                handlePageChange={handlePageChange}
                sortField={sortField}
                sortOrder={sortOrder}
                searchData={searchData}
                searchCriteria={searchCriteria}
              />
            </Col>
          </Row>
        </Container>
      </Section>
      <WebLogImage browserPath pagedata={pagedata} />
    </Layout>
  );
};

function mapStateToProps(state) {
  const { namedParty, auth } = state;
  const {
    isLoggedIn,
    role,
    userinfo: { PersonID },
  } = auth;
  const { fieldOptionsData, sortOrder, sortField, searchData, searchCriteria } = namedParty;
  return {
    isLoggedIn,
    role,
    PersonID,
    fieldOptionsData,
    sortOrder,
    sortField,
    searchData,
    searchCriteria,
  };
}

export default connect(
  mapStateToProps,
  {
    loadResults,
    setCurrentPage,
    setSortField,
    setSortOrder,
    getFieldOptions,
    updateSearchCriteria,
  }
)(NamedParty);
